// frontend/modules/customer-notification/components/dashboard/SmsBalanceCard.tsx
import React from 'react';
import { Card, Statistic, Spin, Typography, Progress, Button } from 'antd';
import { ReloadOutlined, MessageOutlined, WarningOutlined } from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';

const { Text } = Typography;

interface SmsBalanceCardProps {
    balance: number | null;
    totalAllocation?: number;
    isLoading: boolean;
    onRefresh: () => void;
}

const SmsBalanceCard: React.FC<SmsBalanceCardProps> = ({
                                                           balance,
                                                           totalAllocation = 10000, // Default allocation if not provided
                                                           isLoading,
                                                           onRefresh
                                                       }) => {
    const { dark } = useTheme();

    // Calculate percentage of SMS used
    const percentUsed = balance !== null ? Math.round((balance / totalAllocation) * 100) : 0;

    // Determine color based on remaining balance
    const getStatusColor = () => {
        if (balance === null) return '#8c8c8c'; // Gray for loading/unknown
        if (percentUsed < 25) return '#ff4d4f'; // Red for low balance
        if (percentUsed < 50) return '#faad14'; // Yellow/Orange for medium
        return '#52c41a'; // Green for good balance
    };

    return (
        <Card className="mb-4">
            <div className="flex justify-between items-center">
                <div className="flex flex-col">
                    <div className="flex items-center mb-2">
                        <MessageOutlined style={{ fontSize: '24px', marginRight: '8px' }} />
                        <Text strong style={{ fontSize: '18px' }}>SMS Balance</Text>
                    </div>

                    {isLoading ? (
                        <div className="flex items-center">
                            <Spin size="small" className="mr-2" />
                            <Text>Loading SMS balance...</Text>
                        </div>
                    ) : (
                        <>
                            <Statistic
                                value={balance || 0}
                                suffix={`/ ${totalAllocation} messages`}
                                valueStyle={{ color: getStatusColor() }}
                            />

                            <div className="mt-3" style={{ width: '300px' }}>
                                <Progress
                                    percent={percentUsed}
                                    strokeColor={getStatusColor()}
                                    size="small"
                                />
                            </div>

                            {balance !== null && balance < (totalAllocation * 0.25) && (
                                <div className="mt-2 flex items-center">
                                    <WarningOutlined style={{ color: '#ff4d4f', marginRight: '8px' }} />
                                    <Text type="danger">Low balance. Consider purchasing additional SMS credits.</Text>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <Button
                    icon={<ReloadOutlined />}
                    onClick={onRefresh}
                    loading={isLoading}
                >
                    Refresh
                </Button>
            </div>
        </Card>
    );
};

export default SmsBalanceCard;